/**
 * /blog — Public blog listing (Jewel/Luxury theme).
 * Lists all PUBLISHED posts in reverse-chronological order.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Calendar, Tag, Loader2, BookOpen } from "lucide-react";
import { StickyMobileTrialBar } from "./Landing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return ""; }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Blog · AgenteLead Pro — Insights B2B & Growth";
    axios.get(`${API}/blog/posts?limit=50`)
      .then(({ data }) => setPosts(data.posts || []))
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-list-page" className="min-h-screen pb-[68px] lg:pb-0" style={{ background: "#0F172A", color: "#F5F1E8" }}>
      {/* Hero */}
      <section className="relative overflow-hidden border-b" style={{ borderColor: "rgba(132,204,22,0.18)" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 80% 20%, rgba(132,204,22,0.18), transparent 60%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <Link to="/" className="text-[#A1A1AA] hover:text-white text-[12px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-2 mb-6"
            data-testid="blog-back-home">← AgenteLead.Pro</Link>
          <div className="flex items-center gap-2 text-[#84CC16] text-[12px] uppercase tracking-[0.3em] font-bold mb-4">
            <BookOpen className="w-4 h-4" /> Insights & Growth Authority
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 max-w-4xl">
            O futuro das vendas B2B,
            <span className="block" style={{ color: "#84CC16" }}>decifrado em cada artigo.</span>
          </h1>
          <p className="text-base lg:text-lg text-white/70 max-w-2xl leading-relaxed">
            Tendências reais do mercado americano (Forbes, Inc, HBR, TechCrunch) cruzadas com a prática
            de prospecção automatizada — feito pra quem vende todo dia, não pra encher LinkedIn.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        {loading && (
          <div className="flex items-center gap-3 text-white/60" data-testid="blog-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Carregando artigos…
          </div>
        )}
        {error && !loading && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm" data-testid="blog-error">
            {error}
          </div>
        )}
        {!loading && !error && posts.length === 0 && (
          <div className="rounded-xl border p-10 text-center" data-testid="blog-empty"
            style={{ borderColor: "rgba(132,204,22,0.18)", background: "rgba(255,255,255,0.02)" }}>
            <BookOpen className="w-10 h-10 text-[#84CC16] mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white mb-2">Em breve, conteúdos exclusivos.</h2>
            <p className="text-white/60 text-sm">O Content Agent publica novos artigos toda terça e sexta.</p>
          </div>
        )}

        {!loading && !error && posts.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group rounded-xl border p-6 transition-all hover:-translate-y-1 hover:border-[#84CC16]/60"
                style={{ borderColor: "rgba(132,204,22,0.18)", background: "rgba(255,255,255,0.02)" }}>
                <div className="text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#84CC16] mb-3 flex items-center gap-2">
                  <Tag className="w-3 h-3" /> {p.niche || "Insights"}
                </div>
                <h3 className="text-[18px] lg:text-[20px] font-display font-extrabold leading-tight mb-3 text-white group-hover:text-[#84CC16] transition-colors">
                  {p.title}
                </h3>
                <p className="text-[13.5px] text-white/65 leading-relaxed mb-4 line-clamp-3">
                  {p.meta_description}
                </p>
                <div className="flex items-center justify-between text-[11.5px] text-white/45">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> {formatDate(p.published_at)}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[#84CC16] font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                    Ler <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Footer CTA */}
      <section className="border-t" style={{ borderColor: "rgba(132,204,22,0.18)" }}>
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <h2 className="text-2xl lg:text-3xl font-display font-extrabold text-white mb-4">
            Pare de gerenciar planilhas. <span style={{ color: "#84CC16" }}>Comece a fechar negócios.</span>
          </h2>
          <p className="text-white/65 text-sm mb-8">14 dias grátis · sem cartão de crédito</p>
          <Link to="/signup" data-testid="blog-footer-cta"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-md font-extrabold uppercase tracking-tight text-[13px]"
            style={{ background: "#84CC16", color: "#0F172A", boxShadow: "0 8px 30px -10px rgba(132,204,22,0.55)" }}>
            Começar Free Trial <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      <StickyMobileTrialBar lang="pt" variant="blog" />
    </div>
  );
}
